/**
 * Downloads a message attachment to the viewer's device. The attachment is served from blob storage on a
 * different origin via a time-limited link, so a plain `download` anchor would be ignored (browsers only honour
 * `download` same-origin) and would open the image instead. The bytes are therefore fetched into a blob and
 * saved through an object URL, which forces a real download and preserves the file name. The anchor is
 * appended to the document before the click because some browsers ignore a click on a detached anchor, and
 * the object URL is revoked on a deferred tick (not synchronously after the click) because revoking on the
 * same tick can cancel an in-flight download of a large MMS. If the fetch is blocked (for example the blob
 * store's CORS does not permit it), the link is opened in a new tab as a fallback so the click is never inert.
 */
export async function downloadAttachment(url: string | null | undefined, fileName?: string | null): Promise<void> {
    if (!url) {
        return;
    }

    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Attachment fetch failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName || 'attachment';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
        window.open(url, '_blank', 'noopener');
    }
}
